import { useLyrraAccessPad } from '@/hooks/useLyrraAccessPad';
import { useLyrraAccessPadGuideAudio } from '@/hooks/useLyrraAccessPadGuideAudio';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Loader2, Pause, Play } from 'lucide-react';

/**
 * Badge global « Lyrra Access Pad connecté » + guide vocal Mistral (cache serveur par langue).
 */
export function LyrraAccessPadIndicator() {
  const { connected } = useLyrraAccessPad();
  const { t } = useTranslation();
  const { togglePlay, isLoading, isPlaying, errorMessage } = useLyrraAccessPadGuideAudio(connected);

  /**
   * Quand le pad n'est pas connecté, on ne rend rien : évite à la fois
   * le violation a11y `aria-hidden-focus` (bouton focusable dans un
   * container `aria-hidden`) et le bruit DOM/SR inutile. L'animation
   * d'entrée se joue lors du premier mount post-connexion.
   */
  if (!connected) return null;

  return (
    <div
      id="lyrra-access-pad-indicator"
      role="status"
      aria-live="polite"
      className={cn(
        'lyrra-access-pad-indicator fixed left-1/2 z-[9999] flex -translate-x-1/2 items-center gap-2',
        'rounded-full bg-[#0C1C38] py-2.5 pl-3 pr-[18px] text-sm font-semibold text-white',
        'shadow-[0_12px_35px_rgba(12,28,56,0.25)]',
        'bottom-6 transition-[opacity,transform] duration-[250ms] ease-out motion-reduce:transition-none',
      )}
    >
      <button
        type="button"
        onClick={() => void togglePlay()}
        disabled={isLoading}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          'bg-white/20 text-white transition hover:bg-white/30',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF8A3D]',
          'disabled:cursor-wait disabled:opacity-70',
        )}
        aria-label={
          isPlaying ? t('accessibility.accessPad.pauseGuide') : t('accessibility.accessPad.playGuide')
        }
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" aria-hidden />
        ) : (
          <Play className="ms-0.5 h-4 w-4" aria-hidden />
        )}
      </button>
      <span
        className="lyrra-access-pad-indicator__status h-2.5 w-2.5 shrink-0 rounded-full bg-[#FF8A3D]"
        aria-hidden
      />
      <span>{t('accessibility.accessPad.connected')}</span>
      {errorMessage ? (
        <span className="sr-only">{errorMessage}</span>
      ) : null}
    </div>
  );
}
